/**
 * Operations with Fractions & Mixed Numbers generator (6.PAFR.3.6).
 *
 * Positive fractions (proper and, at higher difficulty, mixed numbers) across
 * all four operations. Operands are exact Rationals, so the result is computed
 * exactly and the answer is displayed in lowest terms - the Rational class
 * reduces automatically, so the graded answer is always canonical.
 *
 * Families:
 *   F1: a/b + c/d
 *   F2: a/b - c/d   (arranged so the difference is positive)
 *   F3: a/b * c/d
 *   F4: a/b / c/d   (divide by multiplying by the reciprocal)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "fraction-operations" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly denomMax: number;
  readonly numerMax: number;
  /** Allow numerator > denominator (values above 1 / mixed-number style). */
  readonly allowImproper: boolean;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], denomMax: 6, numerMax: 5, allowImproper: false },
  standard: { families: ["F1", "F2", "F3", "F4"], denomMax: 9, numerMax: 8, allowImproper: false },
  hard: { families: ["F1", "F2", "F3", "F4"], denomMax: 12, numerMax: 11, allowImproper: true },
  challenge: { families: ["F2", "F3", "F4"], denomMax: 12, numerMax: 15, allowImproper: true },
};

/** A positive fraction with the given denominator/numerator ranges. */
function makeFraction(rng: Rng, cfg: Cfg): Rational {
  const den = rng.int(2, cfg.denomMax);
  const maxNum = cfg.allowImproper ? cfg.numerMax : den - 1;
  const num = rng.int(1, Math.max(1, maxNum));
  return rat(num, den);
}

export class FractionOperationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let a = makeFraction(rng, cfg);
      let b = makeFraction(rng, cfg);
      let answer: Rational;
      let opLatex: string;
      let opText: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          answer = a.add(b);
          opLatex = `${a.toLatex()} + ${b.toLatex()}`;
          opText = `${a.toString()} + ${b.toString()}`;
          steps = [
            {
              description: "Rewrite over a common denominator, then add the numerators.",
              latex: `${a.toLatex()} + ${b.toLatex()} = ${answer.toLatex()}`,
            },
            { description: "Reduce to lowest terms.", latex: answer.toLatex() },
          ];
          break;
        }
        case "F2": {
          // Order so the difference is positive (6th grade stays non-negative).
          if (a.lt(b)) [a, b] = [b, a];
          answer = a.sub(b);
          if (answer.isZero()) continue;
          opLatex = `${a.toLatex()} - ${b.toLatex()}`;
          opText = `${a.toString()} - ${b.toString()}`;
          steps = [
            {
              description: "Rewrite over a common denominator, then subtract the numerators.",
              latex: `${a.toLatex()} - ${b.toLatex()} = ${answer.toLatex()}`,
            },
            { description: "Reduce to lowest terms.", latex: answer.toLatex() },
          ];
          break;
        }
        case "F3": {
          answer = a.mul(b);
          opLatex = `${a.toLatex()} \\times ${b.toLatex()}`;
          opText = `${a.toString()} * ${b.toString()}`;
          steps = [
            {
              description: "Multiply the numerators and multiply the denominators.",
              latex: `${a.toLatex()} \\times ${b.toLatex()} = ${answer.toLatex()}`,
            },
            { description: "Reduce to lowest terms.", latex: answer.toLatex() },
          ];
          break;
        }
        case "F4": {
          answer = a.div(b);
          opLatex = `${a.toLatex()} \\div ${b.toLatex()}`;
          opText = `${a.toString()} / ${b.toString()}`;
          steps = [
            {
              description: "Dividing by a fraction is multiplying by its reciprocal.",
              latex: `${a.toLatex()} \\times ${b.reciprocal().toLatex()}`,
            },
            { description: "Multiply, then reduce.", latex: answer.toLatex() },
          ];
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Compute (lowest terms): ${opText}`,
          latex: `${opLatex} = \\;?`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "Add/subtract need a common denominator; multiply/divide do not." },
          { level: 2, text: "To divide, multiply by the reciprocal. Always reduce your answer." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const fractionOperations = new FractionOperationsGenerator();
